import { Icon } from '@ui'

import { isValue, useAccessRole } from '@app/util'
import Schedule from '@app/component/pages/schedule'
import { PERMISSIONS, PERMISSIONS_PRO } from '@app/const/permissions-pool'
import { useIsProd } from '@app/util/hooks/is-admin'
import { useCanUpdateTiers, useIsUsiningTiers, useOrganization } from '@app/util/hooks/tiers-hooks'
import { useOnboarding } from '@app/util/hooks/use-onboarding'
import { isDevApi } from '@app/const/config'

export const useRoutingConfig = ({
  auth,
  isPluginEnabled,
  setModal,
  workspaceId,
  isOrgOwner,
  subscription,
  workspace,
  me
}) => {
  const { canRead, canWrite, orgCanAccess } = useAccessRole()

  const organization = useOrganization()
  const seatLimit = organization?.seatLimit || 0
  const employeeCount = organization?.employeeCount || 0
  const remainingSeats = seatLimit - employeeCount
  const isUsingTiers = useIsUsiningTiers()

  const hasEnoughSeats = isDevApi() || !organization || !isUsingTiers || remainingSeats >= 0
  const isPaidOrTrial =
    (subscription?.subscriptionStatus === 'paid' && hasEnoughSeats) ||
    subscription?.subscriptionStatus === 'trial'

  const isAuthed = !!auth
  const myLocalities = workspace?.localities?.filter(l => l?.assigns?.some(ass => ass.userId === me?.id))
  const limitedToOneLocality = workspace?.enforceLocalities && myLocalities.length === 1

  const onPluginClick = (plugin) => {
    setModal('plugin-detail', { plugin: { plugin } })
  }

  const attendanceModal = !isPluginEnabled('attendance') ? () => onPluginClick('attendance') : null
  const reportsModal = !isPluginEnabled('reports') ? () => onPluginClick('reports') : null
  const laborlawModal = !isPluginEnabled('laborlaw') ? () => onPluginClick('laborlaw') : null
  const offersModal = !isPluginEnabled('offers') ? () => onPluginClick('offers') : null
  const apikeysModal = !isPluginEnabled('apikeys') ? () => onPluginClick('apikeys') : null
  const mfaModal = !isPluginEnabled('mfa') ? () => onPluginClick('mfa') : null
  const dayswapsproModal = !isPluginEnabled('dayswapspro') ? () => onPluginClick('dayswapspro') : null
  const chatModal = !isPluginEnabled('chat') ? () => onPluginClick('chat') : null
  const communicationModal = !isPluginEnabled('communication') ? () => onPluginClick('communication') : null
  const terminalModal = !isPluginEnabled('terminal') ? () => onPluginClick('terminal') : null
  const capacityplanningModal = !isPluginEnabled('capacityplanning') ? () => onPluginClick('capacityplanning') : null

  const isProd = useIsProd()
  const canUpdateTiers = useCanUpdateTiers()
  const { isEligible: isOnboardingEligible } = useOnboarding()

  return ({
    app: [
      {
        // Onboarding checklist for freshly created workspaces (design page 1). `isEligible`
        // is false for every workspace created before the redesign shipped, so this item
        // never appears for an existing customer; it also disappears for good once the
        // manager finishes the checklist. Kept above /schedule so it reads as the first
        // thing to do. See app/util/hooks/use-onboarding.ts.
        allowed: isPaidOrTrial && isOnboardingEligible,
        componentPath: 'onboarding',
        link: '/onboarding',
        path: '/onboarding',
        label: 'ONB_NAV',
        ico: Icon.ICONS.checkboxes
      },
      {
        // Manager dashboard (design page 2)
        allowed: isPaidOrTrial && canRead(PERMISSIONS.MNG_DASHBOARD),
        componentPath: 'manager-dashboard',
        link: '/dashboard',
        path: '/dashboard',
        exact: true,
        label: 'MNG_DASH_TITLE',
        ico: Icon.ICONS.dashboard,
        containerConfig: {
          // The dashboard draws its own header bar and owns its scrolling, so the shell's
          // 1rem gutter would frame the whole page including that bar.
          noPadding: true,
          noScroll: true
        }
      },
      {
        allowed: isPaidOrTrial && canRead(PERMISSIONS.CALENDAR),
        component: Schedule,
        link: '/schedule',
        path: '/schedule',
        label: 'CALENDAR',
        containerConfig: {
          noScroll: true,
          noAnimation: true
        },
        ico: Icon.ICONS.calendar
      },
      {
        allowed: isPaidOrTrial && canRead(PERMISSIONS.CALENDAR),
        component: Schedule,
        path: '/schedule',
        containerConfig: {
          noScroll: true,
          noAnimation: true
        }
      },
      {
        allowed: isPaidOrTrial && canRead(PERMISSIONS.WORKSPACE),
        path: '/workspace',
        label: 'WORKSPACE',
        ico: Icon.ICONS.building,
        subpages: [
          {
            groupTitle: 'SUBNAV_SECTION_STRUCTURE',
            allowed: canRead(PERMISSIONS.WORKSPACE.EMPLOYEES),
            componentPath: 'workspace/employees',
            path: '/employees',
            label: 'EMPLOYEES',
            ico: Icon.ICONS.people
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.CUSTOMATTRIBUTES),
            componentPath: 'workspace/custom-attributes',
            path: '/custom-attributes',
            label: 'CUSTOM_ATTRIBUTES',
            ico: Icon.ICONS.badge
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.POSITIONS),
            componentPath: 'workspace/positions',
            path: '/job-positions',
            label: 'POSITIONS',
            ico: Icon.ICONS.positions
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.LOCALITIES),
            componentPath: 'workspace/localities',
            path: '/localities',
            label: 'LOCALITIES',
            ico: Icon.ICONS.people
          },
          {
            groupTitle: 'SUBNAV_SECTION_PLANNING',
            allowed: canRead(PERMISSIONS.WORKSPACE.TIMEOFFCATEGORIES),
            componentPath: 'workspace/timeoff-categories',
            path: '/timeoff-categories',
            label: 'WS_SETTINGS_CARD_UNAV_CATEGORIES_NAV',
            ico: Icon.ICONS.absence
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.SHIFTTEMPLATES),
            componentPath: 'workspace/shift-templates',
            path: '/shift-templates',
            label: 'SHIFT_TEMPLATES',
            ico: Icon.ICONS.template
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.SHIFTTEMPLATES),
            componentPath: 'workspace/day-templates/editor',
            path: '/day-templates/:action/:id'
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.SHIFTTEMPLATES),
            componentPath: 'workspace/day-templates/editor',
            path: '/day-templates/:action'
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.DAYTEMPLATES),
            componentPath: 'workspace/day-templates',
            path: '/day-templates',
            label: 'DAY_TEMPLATES',
            ico: Icon.ICONS.template2
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.CYCLES),
            componentPath: 'workspace/cycles/editor',
            path: '/cycles/:action/:id'
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.CYCLES),
            componentPath: 'workspace/cycles/editor',
            path: '/cycles/:action'
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.CYCLES),
            componentPath: 'workspace/cycles',
            path: '/cycles',
            label: 'CYCLES',
            ico: Icon.ICONS.cycle
          },
          // {
          //   allowed: canRead(PERMISSIONS.WORKSPACE.SHIFTATTRIBUTES),
          //   componentPath: 'settings/attributes',
          //   path: '/attributes',
          //   label: 'ATTRIBUTES_NAV_NAME',
          //   ico: Icon.ICONS.badge
          // },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.SHIFTATTRIBUTES),
            componentPath: 'workspace/shift-attributes',
            path: '/shift-attributes',
            label: 'SHIFT_ATTRIBUTES_NAV_NAME',
            ico: Icon.ICONS.badge
          },
          {
            allowed: canRead(PERMISSIONS.WORKSPACE.GROUPS),
            componentPath: 'workspace/groups',
            path: '/groups',
            label: 'GROUPS_TITLE',
            ico: Icon.ICONS.covid
          }
        ]
      },
      {
        allowed: isPaidOrTrial && canRead(PERMISSIONS.BACKOFFICE),
        // DECIDE -> by default it will always pick attendance first, but in case attendance is not allowed it should pick unavailability requests
        // There's also a backup for unavailability requests when attendance is not enabled
        // however there is special case when attendance is enabled but user can't access it
        // and I don't want to write complicated logic here which could just change down the line
        // CTRL+F 'DECIDE' in app/component/core/nav/Navigation.js
        link: isPluginEnabled('attendance') ? 'DECIDE'/* '/backoffice/attendance' */ : '/backoffice/unavailability-requests',
        path: '/backoffice',
        label: 'BACKOFFICE',
        ico: Icon.ICONS.computer,
        subpages: [
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.ATTENDANCE),
            disabled: !isPluginEnabled('attendance'),
            componentPath: 'backoffice/attendance',
            path: '/attendance',
            label: 'ATTENDANCE',
            ico: Icon.ICONS.attendance,
            onClick: attendanceModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.BONUSES),
            disabled: !isPluginEnabled('attendance'),
            componentPath: 'backoffice/bonuses',
            path: '/bonuses',
            label: 'BONUSES',
            ico: Icon.ICONS.cash,
            onClick: attendanceModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.MEAL_VOUCHERS) && (!isProd) && canRead(PERMISSIONS.CALENDAR),
            disabled: !isPluginEnabled('dayswapspro'),
            componentPath: 'backoffice/meal-vouchers',
            path: '/meal-vouchers',
            label: 'ORG_BONUSES_MEAL_TICKETS',
            ico: Icon.ICONS.break,
            onClick: dayswapsproModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.TERMINALS),
            disabled: !isPluginEnabled('terminal'),
            onClick: terminalModal,
            componentPath: 'backoffice/terminals',
            path: '/terminals',
            label: 'TERMINAL',
            ico: Icon.ICONS.terminal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.TIMEOFFREQUESTS),
            groupTitle: 'BACKOFFICE_REQUESTS',
            componentPath: 'backoffice/unavailability-requests',
            path: '/unavailability-requests',
            label: 'BACKOFFICE_UNAVAILABILITY_REQUESTS',
            ico: Icon.ICONS.absence
          },
          {
            groupTitle: 'REPORTS',
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_EMPLOYEE),
            disabled: !isPluginEnabled('reports'),
            componentPath: 'backoffice/reports/employee',
            path: '/reports/emp',
            label: 'EMPLOYEES',
            ico: Icon.ICONS.person,
            onClick: reportsModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_WORKSPACE) && !limitedToOneLocality,
            disabled: !isPluginEnabled('reports'),
            componentPath: 'backoffice/reports/workspace',
            path: '/reports/ws',
            label: 'WORKSPACE',
            ico: Icon.ICONS.home,
            onClick: reportsModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_WARNINGS),
            disabled: !isPluginEnabled('laborlaw'),
            componentPath: 'backoffice/reports/warnings',
            path: '/reports/warnings',
            label: 'WARNINGS_TITLE',
            ico: Icon.ICONS.law,
            onClick: laborlawModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_OVERTIME),
            disabled: !isPluginEnabled('reports'),
            componentPath: 'backoffice/reports/overtime',
            path: '/reports/overtime',
            label: 'REPORTS_OVERTIME',
            ico: Icon.ICONS.star,
            onClick: reportsModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_CONTACTS),
            disabled: !isPluginEnabled('reports'),
            componentPath: 'backoffice/reports/covid',
            path: '/reports/coworker-contacts',
            label: 'COVID_REPORT_TITLE_SHORT',
            ico: Icon.ICONS.covid,
            onClick: reportsModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_REVENUE),
            disabled: !isPluginEnabled('dayswapspro') || !isPluginEnabled('reports'),
            // Temporarily showing the legacy table instead of the new one
            // componentPath: 'backoffice/reports/revenue/workspace-performance',
            componentPath: 'backoffice/reports/revenue',
            path: '/reports/revenue',
            label: 'ORG_SP_WORKSPACES_PERFORMANCE',
            ico: Icon.ICONS.performance,
            onClick: !isPluginEnabled('dayswapspro') ? dayswapsproModal : reportsModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_EMPLOYEE),
            disabled: !isPluginEnabled('reports'),
            path: '/reports/inspection',
            componentPath: 'backoffice/reports/inspection',
            label: 'INSPECTION',
            ico: Icon.ICONS.law,
            onClick: reportsModal
          },
          {
            allowed: canRead(PERMISSIONS.BACKOFFICE.REPORT_FORMS),
            disabled: !isPluginEnabled('dayswapspro'),
            componentPath: 'backoffice/reports/forms',
            path: '/reports/forms',
            label: 'FORMS',
            ico: Icon.ICONS.clipboard,
            onClick: dayswapsproModal
          }
        ]
      },

      {
        allowed: isPaidOrTrial && canRead(PERMISSIONS.SETTINGS),
        link: '/settings/basic',
        path: '/settings',
        label: 'SETTINGS',
        atBottom: true,
        ico: Icon.ICONS.settings,
        subpages: [
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSBASIC),
            componentPath: 'settings/basic',
            path: '/basic',
            label: 'WS_SETTINGS_CARD_BASIC_SETTINGS_NAV',
            ico: Icon.ICONS.settingsFull
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSPERMISSIONS),
            componentPath: 'settings/permissions/Permissions',
            path: '/permissions',
            label: 'PERMISSIONS',
            ico: Icon.ICONS.settings2
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSBASIC),
            componentPath: 'settings/mfa',
            path: '/mfa',
            label: 'MFA',
            disabled: !isPluginEnabled('mfa'),
            onClick: mfaModal,
            ico: Icon.ICONS.lock
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSNOTIFICATIONS),
            componentPath: 'settings/notifications',
            path: '/notifications',
            label: 'NOTIFICATION',
            ico: Icon.ICONS.bell
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSEMPLOYEES),
            componentPath: 'settings/employee',
            path: '/employee-preferences',
            label: 'EMPLOYEES',
            ico: Icon.ICONS.person
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSOFFERS),
            disabled: !isPluginEnabled('offers'),
            componentPath: 'settings/offers',
            path: '/contest-settings',
            label: 'OFFERS',
            ico: Icon.ICONS.offer,
            onClick: offersModal
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSCAPACITIES),
            disabled: !isPluginEnabled('capacityplanning'),
            onClick: capacityplanningModal,
            componentPath: 'settings/capacities',
            path: '/capacities',
            label: 'CAPACITY',
            ico: Icon.ICONS.capacity
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.SETTINGSATTENDANCETERMINALS),
            disabled: (!isPluginEnabled('attendance') && !isPluginEnabled('terminal')),
            componentPath: 'settings/AttendanceAndTerminals',
            path: '/attendance-and-terminals',
            label: 'WS_SETTINGS_ATTENDANCE_AND_TERMINALS',
            ico: Icon.ICONS.attendance,
            onClick: (!isPluginEnabled('attendance') && !isPluginEnabled('terminal')) ? attendanceModal : null
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.APIKEYS),
            disabled: !isPluginEnabled('apikeys'),
            componentPath: 'settings/apikeys',
            path: '/api-keys',
            label: 'WS_SETTINGS_APIKEYS_HEADER',
            ico: Icon.ICONS.api,
            onClick: apikeysModal
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.AUDITLOGS),
            componentPath: 'settings/AuditLog',
            path: '/audit-log',
            label: 'WS_SETTINGS_AUDIT_LOG',
            ico: Icon.ICONS.law
          },
          {
            groupTitle: 'WS_SETTINGS_CARD_PLUGINS_SETTINGS',
            allowed: canRead(PERMISSIONS.SETTINGS.PLUGINS),
            componentPath: 'settings/plugin-settings',
            path: '/internal',
            label: 'DAYSWAPS',
            ico: Icon.ICONS.dayswaps
          },
          {
            allowed: canRead(PERMISSIONS.SETTINGS.PLUGINS),
            componentPath: 'settings/plugin-settings',
            path: '/external',
            label: 'ORG_SP_SETTINGS_MODULES_EXTERNAL',
            ico: Icon.ICONS.external
          }
        ]
      },
      {
        allowed: canRead(PERMISSIONS.EMP_CALENDAR) && isValue(workspaceId),
        componentPath: 'my-calendar-2',
        path: '/my-timetable',
        label: 'MY_EMP_CALENDAR',
        ico: Icon.ICONS.calendar,
        hidden: canWrite(PERMISSIONS.MNG_CALENDAR)
      },
      {
        allowed: canRead(PERMISSIONS.EMP_ATTENDANCE) && Boolean(workspaceId) && isPluginEnabled('attendance'),
        componentPath: 'my-attendance',
        path: '/my-attendance',
        label: 'MY_ATTENDANCE',
        ico: Icon.ICONS.attendance,
        hidden: canWrite(PERMISSIONS.MNG_CALENDAR)
      },
      {
        allowed: isPaidOrTrial && canRead(PERMISSIONS.COMMUNICATION) && (
          (isPluginEnabled('communication') && (canRead(PERMISSIONS.EMP_CALENDAR) || canRead(PERMISSIONS.MNG_CALENDAR))) || (isPluginEnabled('chat') && me?.chatToken)
        ),
        link: isPluginEnabled('chat') && me?.chatToken && canRead(PERMISSIONS.COMMUNICATION.CHAT) ? '/communication/chat' : '/communication/news-feed',
        path: '/communication',
        label: 'COMMUNICATION',
        ico: Icon.ICONS.chat,
        subpages: [
          {
            allowed: (canRead(PERMISSIONS.COMMUNICATION.CHAT)),
            disabled: !isPluginEnabled('chat'),
            onClick: chatModal,
            componentPath: 'communication/chat',
            path: '/chat/:channelId'
          },
          {
            allowed: (canRead(PERMISSIONS.COMMUNICATION.CHAT)),
            disabled: !isPluginEnabled('chat'),
            onClick: chatModal,
            componentPath: 'communication/chat',
            path: '/chat',
            label: 'CHAT',
            ico: Icon.ICONS.chat
          },
          {
            allowed: canRead(PERMISSIONS.COMMUNICATION.NEWS_FEED) && (
              ((canRead(PERMISSIONS.EMP_CALENDAR) || canRead(PERMISSIONS.MNG_CALENDAR)))
            ),
            disabled: !isPluginEnabled('communication'),
            onClick: communicationModal,
            componentPath: 'communication/news-feed',
            path: '/news-feed',
            label: 'NEWS_FEED',
            ico: Icon.ICONS.comment
          }]
      },
      {
        // linked from the "Fakturace" nav item and the licence counter, not listed on its own
        allowed: canUpdateTiers,
        componentPath: 'invoicing',
        path: '/invoicing'
      },
      {
        allowed: isAuthed && !!workspaceId,
        componentPath: 'my-notification',
        path: '/my-notification',
        label: null,
        hidden: !isValue(workspaceId)
      }
    ],
    pro: [
      {
        allowed: isAuthed && isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
        componentPath: 'organization/workspaces/performance',
        // Path must NOT be a substring of `/settings/performance` — the nav
        // resolves the active section via `pathname.includes(route.path)`, so a
        // top-level `/performance` would shadow the settings sub-page's subnav.
        path: '/performance-overview',
        label: 'ORG_SP_WORKSPACES_PERFORMANCE',
        ico: Icon.ICONS.performance
      },
      {
        allowed: isAuthed && isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.WORKSPACES),
        path: '/workspaces',
        label: 'WORKSPACES',
        ico: Icon.ICONS.home,
        subpages: [
          {
            groupTitle: 'SUBNAV_SECTION_STRUCTURE',
            path: '/countries',
            componentPath: 'organization/workspaces/Countries',
            label: 'ORG_SP_WORKSPACES_COUNTRIES',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.COUNTRIES),
            ico: Icon.ICONS.country
          },
          {
            path: '/areas',
            componentPath: 'organization/workspaces/Areas',
            label: 'ORG_SP_WORKSPACES_AREAS',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.AREAS),
            ico: Icon.ICONS.area
          },
          {
            path: '/workplace',
            componentPath: 'organization/workspaces/Workspace',
            label: 'WORKSPACES',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.WORKSPACES),
            ico: Icon.ICONS.home
          },
          {
            path: '/positions',
            componentPath: 'organization/workspaces/Positions',
            label: 'POSITIONS',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.POSITIONS),
            ico: Icon.ICONS.positions
          },
          {
            path: '/access-roles',
            componentPath: 'organization/workspaces/access-roles',
            label: 'PERMISSIONS',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.POSITIONS),
            ico: Icon.ICONS.access
          },
          {
            groupTitle: 'SETTINGS',
            path: '/plan',
            componentPath: 'organization/workspaces/RevenuePlan',
            label: 'ORG_SP_WORKSPACES_PLAN_REVENUE',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.REVENUE),
            ico: Icon.ICONS.turnover
          },
          {
            path: '/norms',
            label: 'ORG_SP_WORKSPACES_NORMS',
            componentPath: 'organization/workspaces/Norms',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.NORMS),
            ico: Icon.ICONS.standards
          },
          {
            path: '/productivity',
            componentPath: 'organization/workspaces/Productivity',
            label: 'ORG_SP_WORKSPACES_PRODUCTIVITY',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PRODUCTION),
            ico: Icon.ICONS.productivity
          },
          {
            path: '/fulltime-plan',
            componentPath: 'organization/workspaces/FulltimePlan',
            label: 'ORG_SP_WORKSPACES_FULLTIME_PLAN',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PLANS),
            ico: Icon.ICONS.hoursDivision
          },
          {
            path: '/holidays',
            componentPath: 'organization/workspaces/Holidays',
            label: 'ORG_SP_WORKSPACES_HOLIDAYS',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.HOLIDAYS),
            ico: Icon.ICONS.holidays
          },
          {
            path: '/important-days',
            componentPath: 'organization/workspaces/important-days',
            label: 'IMPORTANT_DAYS',
            allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.HOLIDAYS),
            ico: Icon.ICONS.holidays
          }
          // Temporarily hidden
          // ,{
          //   path: '/vacation-prescription',
          //   componentPath: 'organization/workspaces/vacation-prescription',
          //   label: 'VACATION_PRESCRIPTION',
          //   allowed: orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
          //   ico: Icon.ICONS.vacation
          // }

        ]
      },
      {
        allowed: isAuthed && isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.TRANSFERS),
        path: '/transfers',
        label: 'SB_TRANSFERS',
        ico: Icon.ICONS.exchange,
        subpages: [
          {
            path: '/overview',
            label: 'ORG_SP_TRANSFERS_OVERVIEW',
            componentPath: 'organization/transfers/Overview',
            allowed: orgCanAccess(PERMISSIONS_PRO.TRANSFERS.OVERVIEW),
            ico: Icon.ICONS.overview
          },
          {
            path: '/details',
            label: 'SB_TRANSFERS',
            componentPath: 'organization/transfers/Details',
            allowed: orgCanAccess(PERMISSIONS_PRO.TRANSFERS.DETAILS),
            ico: Icon.ICONS.exchange
          },
          {
            groupTitle: 'SETTINGS',
            path: '/groups',
            componentPath: 'organization/transfers/Groups',
            label: 'GROUPS',
            allowed: orgCanAccess(PERMISSIONS_PRO.TRANSFERS.GROUPS),
            ico: Icon.ICONS.people
          }
        ]
      },
      {
        allowed: isAuthed && isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.PAYROLL),
        path: '/bonuses',
        label: 'ORG_SP_WAGES',
        ico: Icon.ICONS.cash,
        subpages: [
          {
            path: '/locked-until',
            label: 'APPROVED',
            componentPath: 'organization/bonuses/LockedUntil',
            allowed: (orgCanAccess(PERMISSIONS_PRO.PAYROLL.ATTENDANCE) || orgCanAccess(PERMISSIONS_PRO.PAYROLL.ATTENDANCE_LVL2) || orgCanAccess(PERMISSIONS_PRO.PAYROLL.ATTENDANCE_LVL3)),
            ico: Icon.ICONS.template
          },
          {
            path: '/attendance',
            label: 'ATTENDANCE',
            componentPath: 'organization/bonuses/attendance',
            allowed: (orgCanAccess(PERMISSIONS_PRO.PAYROLL.ATTENDANCE) || orgCanAccess(PERMISSIONS_PRO.PAYROLL.ATTENDANCE_LVL2) || orgCanAccess(PERMISSIONS_PRO.PAYROLL.ATTENDANCE_LVL3)),
            ico: Icon.ICONS.overview
          },
          {
            path: '/overview',
            label: 'BONUSES',
            componentPath: 'organization/bonuses/Overview',
            allowed: orgCanAccess(PERMISSIONS_PRO.PAYROLL.BONUSES_OVERVIEW),
            ico: Icon.ICONS.cash
          },
          {
            allowed: orgCanAccess(PERMISSIONS_PRO.PAYROLL.BONUSES_OVERVIEW) && (!isProd),
            // disabled: !isPluginEnabled('attendance'),
            componentPath: 'backoffice/meal-vouchers',
            path: '/meal-vouchers',
            label: 'MEAL_TICKETS',
            ico: Icon.ICONS.break
            // onClick: attendanceModal
          },
          {
            groupTitle: 'SETTINGS',
            path: '/sale',
            label: 'ORG_BONUSES_SALE',
            componentPath: 'organization/bonuses/Sale',
            allowed: orgCanAccess(PERMISSIONS_PRO.PAYROLL.BONUSES_SALES),
            ico: Icon.ICONS.items
          },
          {
            path: '/recovery',
            label: 'ORG_BONUSES_RECOVERY',
            componentPath: 'organization/bonuses/Recovery',
            // TODO: Add new permission bonuses_recovery
            allowed: orgCanAccess(PERMISSIONS_PRO.PAYROLL.BONUSES_SALES),
            ico: Icon.ICONS.items
          },
          {
            path: '/meal-tickets',
            label: 'ORG_BONUSES_MEAL_TICKETS',
            componentPath: 'organization/bonuses/MealTickets',
            allowed: orgCanAccess(PERMISSIONS_PRO.PAYROLL.BONUSES_MEAL_VOUCHERS),
            ico: Icon.ICONS.items
          },
          {
            path: '/stocktaking',
            label: 'ORG_BONUSES_STOCKTAKING',
            componentPath: 'organization/bonuses/Stocktaking',
            allowed: orgCanAccess(PERMISSIONS_PRO.PAYROLL.BONUSES_INVENTORY),
            ico: Icon.ICONS.items
          }
        ]
      },
      {
        allowed: isAuthed && isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.REPORTS),
        path: '/reports',
        label: 'REPORTS',
        ico: Icon.ICONS.report,
        subpages: [
          {
            allowed: true,
            path: '/employee-report',
            label: 'EMPLOYEES',
            componentPath: 'organization/reports/EmployeeReport',
            ico: Icon.ICONS.person
          },
          {
            allowed: true,
            path: '/workspace-report',
            label: 'WORKSPACE',
            componentPath: 'organization/reports/WorkspaceReport',
            ico: Icon.ICONS.home
          },
          {
            allowed: true,
            path: '/overtime-report',
            label: 'REPORTS_OVERTIME',
            componentPath: 'backoffice/reports/overtime',
            ico: Icon.ICONS.star
          },
          {
            allowed: true,
            path: '/warnings-report',
            label: 'WARNINGS_TITLE',
            componentPath: 'backoffice/reports/warnings',
            ico: Icon.ICONS.law
          },
          {
            allowed: true,
            path: '/inspection-report',
            label: 'INSPECTION',
            componentPath: 'backoffice/reports/inspection',
            ico: Icon.ICONS.law
          }
        ]
      },
      {
        allowed: isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.FORMS.READ),
        label: 'FORMS',
        path: '/forms',
        ico: Icon.ICONS.clipboard,
        subpages: [
          {
            allowed: true,
            path: '/overview',
            label: 'OVERVIEW',
            componentPath: 'organization/forms/overview',
            ico: Icon.ICONS.country
          },
          {
            allowed: true,
            path: '/report',
            label: 'REPORT',
            componentPath: 'organization/forms/report',
            ico: Icon.ICONS.area
          }
        ]
      },
      {
        allowed: isAuthed && isPaidOrTrial && (isOrgOwner || orgCanAccess(PERMISSIONS_PRO.PEOPLE)),
        path: '/people',
        label: 'ORG_SP_PEOPLE',
        ico: Icon.ICONS.people,
        subpages: [
          {
            allowed: true,
            path: '/country-manage',
            label: 'ORG_SP_COUNTRY_MANAGER',
            componentPath: 'organization/people/CountryManager',
            ico: Icon.ICONS.countryManager
          },
          {
            allowed: true,
            path: '/area-manage',
            label: 'ORG_SP_AREA_MANAGER',
            componentPath: 'organization/people/AreaManager',
            ico: Icon.ICONS.areaManager
          },
          {
            allowed: true,
            path: '/workspace-manage',
            label: 'ORG_SP_WORKSPACE_MANAGER',
            componentPath: 'organization/people/WorkspaceManager',
            ico: Icon.ICONS.shopManager
          },
          {
            allowed: true,
            path: '/accountant',
            label: 'ORG_SP_PEOPLE_ACCOUNTANT',
            componentPath: 'organization/people/Accountant',
            ico: Icon.ICONS.accountant
          }
        ]
      },
      {
        allowed: isPaidOrTrial && orgCanAccess(PERMISSIONS_PRO.SETTINGS),
        path: '/settings',
        label: 'SETTINGS',
        atBottom: true,
        ico: Icon.ICONS.settings,
        subpages: [
          // Temporarily hidden: performance settings subpages
          // {
          //   allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS) && orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
          //   componentPath: 'organization/workspaces/performance-data-sources',
          //   path: '/sources',
          //   label: 'SOURCES_AND_CALCULATIONS',
          //   ico: Icon.ICONS.computer
          // },
          // {
          //   allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS) && orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
          //   componentPath: 'organization/workspaces/performance-columns',
          //   path: '/performance',
          //   label: 'ORG_SP_WORKSPACES_PERFORMANCE',
          //   ico: Icon.ICONS.performance
          // },
          // {
          //   allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS) && orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
          //   componentPath: 'organization/workspaces/performance-notifications',
          //   path: '/notifications-performance',
          //   label: 'NOTIFICATIONS',
          //   ico: Icon.ICONS.bell
          // },
          // {
          //   allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS) && orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
          //   componentPath: 'organization/workspaces/performance-notification-alerts',
          //   path: '/notifications-alerts-performance',
          //   label: 'PERFORMANCE_NOTIFICATION_ALERTS',
          //   ico: Icon.ICONS.warning
          // },
          // {
          //   allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS) && orgCanAccess(PERMISSIONS_PRO.WORKSPACES.PERFORMANCE),
          //   componentPath: 'organization/workspaces/performance-notification-logs',
          //   path: '/notifications-logs-performance',
          //   label: 'PERFORMANCE_NOTIFICATION_LOGS',
          //   ico: Icon.ICONS.history
          // },
          {
            allowed: isOrgOwner,
            groupTitle: 'ORG_SP_PEOPLE',
            path: '/permissions',
            componentPath: 'organization/people/Permissions',
            label: 'ORG_SP_PEOPLE_PERMISSIONS',
            ico: Icon.ICONS.access
          },
          {
            allowed: isOrgOwner,
            path: '/admins',
            label: 'ORG_SP_PEOPLE_ADMINS',
            componentPath: 'organization/people/Admins',
            ico: Icon.ICONS.group
          },
          {
            allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS),
            groupTitle: 'ORG_SP_WAGES',
            path: '/meal-vouchers',
            label: 'ORG_BONUSES_MEAL_TICKETS',
            componentPath: 'organization/settings/meal-vouchers',
            ico: Icon.ICONS.items
          },
          {
            allowed: orgCanAccess(PERMISSIONS_PRO.SETTINGS),
            groupTitle: 'WS_SETTINGS_AUDIT_LOG',
            path: '/log',
            label: 'WS_SETTINGS_AUDIT_LOG',
            componentPath: 'organization/settings/log',
            ico: Icon.ICONS.home
          }
        ]
      },
      {
        // linked from the "Fakturace" nav item and the licence counter, not listed on its own
        allowed: canUpdateTiers,
        componentPath: 'invoicing',
        path: '/invoicing'
      },
      {
        allowed: isAuthed,
        componentPath: 'my-notification',
        path: '/my-notification',
        label: null
      }
    ],
    additional: [
      {
        allowed: isOrgOwner || canRead(PERMISSIONS.SUBSCRIPTION) || canWrite(PERMISSIONS.SUBSCRIPTION) || canRead(PERMISSIONS.SETTINGS),
        componentPath: 'paywall',
        path: '/paywall'
      },
      {
        // the link in the `user.forgottenPassword` e-mail — the `?token=` param name is
        // baked into the backend's URL builder, so it must not be renamed
        allowed: true,
        componentPath: 'auth/reset-password',
        path: '/reset-password',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      },
      {
        allowed: true,
        componentPath: 'auth/accept-invite',
        path: '/accept-invite',
        containerConfig: {
          noPadding: true,
          hideNav: true
        }
      },
      {
        // The backend's DEFAULT SSO landing — where the callback goes when it cannot
        // read the state entry and so cannot know where the flow started (the
        // provider's own `?error=`, and `invalid_state`). Every other callback
        // parameter comes back on the `returnTo` the hop was started from and is
        // intercepted in Routes.js instead.
        //
        // MUST stay above '/auth/:token' — that route would otherwise match /auth/sso
        // and try to log in with "sso" as an admin magic-link token
        allowed: true,
        componentPath: 'auth/sso-callback',
        path: '/auth/sso',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      },
      {
        allowed: true,
        componentPath: 'admin-redirect',
        path: '/auth/:token',
        containerConfig: {
          hideNav: true
        }
      },
      {
        allowed: true,
        componentPath: 'admin-redirect',
        path: '/ws/:wsId'
      },
      {
        allowed: true,
        componentPath: 'cycle-presets',
        path: '/cycle-presets',
        containerConfig: {
          hideNav: true
        }
      },
      {
        allowed: true,
        componentPath: 'auth/registration',
        path: '/registration/:step',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      },
      {
        allowed: true,
        componentPath: 'auth/registration',
        path: '/registration',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      },
      {
        // the link in the `user.emailVerify` e-mail — public, and the mutation it calls
        // returns a session, so it must stay reachable without auth
        allowed: true,
        componentPath: 'auth/verify-email',
        path: '/verify-email',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      },
      {
        allowed: auth,
        componentPath: 'no-workspace',
        path: '/no-workspace',
        containerConfig: {
          hideNav: false // this cannot be enabled, because we have some users with no WS, but with ORG roles and they need Navigation to get to DS PRO (or to manage their account)
        }
      },
      {
        allowed: true,
        componentPath: 'terminal',
        path: '/t',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      },
      {
        allowed: true,
        componentPath: 'terminal',
        path: 't/:wsId/:terminalId',
        containerConfig: {
          hideNav: true,
          noPadding: true
        }
      }
    ]
  })
}
